import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Box, Grid, CircularProgress, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import { DashboardHeader } from './DashboardHeader'
import { PortfolioTable } from './PortfolioTable'
import { KPICard } from './KPICard'
import { DeadlinePropertyDrawer } from './DeadlinePropertyDrawer'
import { AddDeadlineModal } from '../../../ScheduleHome/ViewComponents/AddDeadlineModal/AddDeadlineModal'
import { BulkDeadlineModal } from './BulkDeadlineModal'
import { defaultFormState } from '../../../ScheduleHome/types'
import { toast } from 'react-toastify'
import {
    useUser,
    usePagination,
    useDeadline,
    CreateDeadlineRequest,
    CreateBulkDeadlinesRequest,
} from '../../../../hooks'
import {
    useEZFolioDeadlines,
    ApartmentDeadlineData,
} from '../../../../hooks/useEZFolioDeadlines'
import { useUserTags } from '../../../../contexts/UserTagsContext'

type SortKey =
    | 'name'
    | 'progress'
    | 'completedTasks'
    | 'overdueTasks'
    | 'status'
type SortDirection = 'asc' | 'desc'

// Property interface for UI display
export interface Property {
    id: number
    name: string
    location: string
    completedTasks: number
    totalTasks: number
    overdueTasks: number
    status: 'on-track' | 'at-risk' | 'behind'
    region: string
}

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        padding: theme.spacing(4),
        width: '100%',
        flex: 1,
    },
    kpiGrid: {
        marginBottom: theme.spacing(1),
    },
    tableContainer: {
        marginTop: theme.spacing(1),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
}))

// Helper function to calculate date range from time period
const getDateRangeFromTimePeriod = (
    timePeriod: string,
    customStartDate?: Date,
    customEndDate?: Date,
): { startDate: string; endDate: string } => {
    const today = new Date()
    let endDate = new Date(today)
    endDate.setHours(23, 59, 59, 999) // End of today

    let startDate = new Date(today)

    switch (timePeriod) {
        case '7days':
            startDate.setDate(today.getDate() - 7)
            break
        case '30days':
            startDate.setDate(today.getDate() - 30)
            break
        case 'quarter': {
            const currentQuarter = Math.floor(today.getMonth() / 3)
            startDate = new Date(today.getFullYear(), currentQuarter * 3, 1)
            break
        }
        case 'year':
            startDate = new Date(today.getFullYear(), 0, 1)
            break
        case 'custom': {
            // Use custom dates only if both are valid; otherwise default to last 30 days
            const startValid =
                customStartDate && !isNaN(customStartDate.getTime())
            const endValid = customEndDate && !isNaN(customEndDate.getTime())
            if (startValid && endValid) {
                startDate = new Date(customStartDate!)
                endDate = new Date(customEndDate!)
                endDate.setHours(23, 59, 59, 999)
            } else {
                startDate.setDate(today.getDate() - 30)
            }
            break
        }
        default:
            startDate.setDate(today.getDate() - 30)
    }

    startDate.setHours(0, 0, 0, 0) // Start of day

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    }
}

// Helper function to calculate status based on completion percentage
const calculateStatus = (
    completed: number,
    total: number,
    overdue: number,
): 'on-track' | 'at-risk' | 'behind' => {
    if (total === 0) return 'on-track'
    const completionPercentage = (completed / total) * 100

    if (overdue > 0) {
        return 'behind'
    } else if (completionPercentage >= 90) {
        return 'on-track'
    } else if (completionPercentage >= 70) {
        return 'at-risk'
    } else {
        return 'behind'
    }
}

// Map backend data to Property format
const mapDeadlineToProperty = (
    deadline: ApartmentDeadlineData,
): Property | null => {
    // Safety check: ensure id exists
    if (!deadline || deadline.id === undefined || deadline.id === null) {
        return null
    }

    const completedTasks = deadline.total_deadlines_completed || 0
    const totalTasks = deadline.total_deadlines || 0
    const overdueTasks = deadline.total_deadlines_overdue || 0

    return {
        id: deadline.id,
        name: deadline.name || '',
        location: '', // Not available in backend data
        completedTasks,
        totalTasks,
        overdueTasks,
        status: calculateStatus(completedTasks, totalTasks, overdueTasks),
        region: '', // Not available in backend data
    }
}

export const DeadlineHome = () => {
    const theme = useTheme()
    const { organization } = useUser()
    const {
        deadlines,
        loading,
        getApartmentDeadlineData,
    } = useEZFolioDeadlines()
    const {
        createDeadline,
        createBulkDeadlines,
        loadingState: deadlineLoadingState,
    } = useDeadline()
    const { userTags } = useUserTags()
    const classes = useStyles()
    const [addDeadlineModalOpen, setAddDeadlineModalOpen] = useState(false)
    const [bulkDeadlineModalOpen, setBulkDeadlineModalOpen] = useState(false)
    const [timePeriod, setTimePeriod] = useState('30days')
    const [customStartDate, setCustomStartDate] = useState<Date | null>(() => {
        const date = new Date()
        date.setDate(date.getDate() - 30)
        return date
    })
    const [customEndDate, setCustomEndDate] = useState<Date | null>(() => {
        return new Date()
    })
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [sortKey, setSortKey] = useState<SortKey>('name')
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const [selectedProperty, setSelectedProperty] = useState<Property | null>(
        null,
    )
    const [
        selectedDeadlineData,
        setSelectedDeadlineData,
    ] = useState<ApartmentDeadlineData | null>(null)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)

    // Calculate date range from time period
    const dateRange = useMemo(() => {
        return getDateRangeFromTimePeriod(
            timePeriod,
            customStartDate || undefined,
            customEndDate || undefined,
        )
    }, [timePeriod, customStartDate, customEndDate])

    // Form state for AddDeadlineModal (only meta.date and modals.addDeadlineModal are used)
    const addDeadlineFormState = useMemo(
        () => ({
            ...defaultFormState,
            meta: {
                ...defaultFormState.meta,
                date: DateTime.now(),
            },
            modals: {
                ...defaultFormState.modals,
                addDeadlineModal: addDeadlineModalOpen,
            },
        }),
        [addDeadlineModalOpen],
    )

    const handleCreateDeadline = useCallback(
        (request: CreateDeadlineRequest) => {
            return createDeadline(request).then((result) => {
                if (organization) {
                    getApartmentDeadlineData(
                        organization,
                        dateRange.startDate,
                        dateRange.endDate,
                    )
                }
                toast.success('Deadline created')
                return result
            })
        },
        [
            createDeadline,
            organization,
            dateRange.startDate,
            dateRange.endDate,
            getApartmentDeadlineData,
        ],
    )

    const handleBulkCreateDeadlines = useCallback(
        (request: CreateBulkDeadlinesRequest) => {
            return createBulkDeadlines(request)
                .then((created) => {
                    if (organization) {
                        getApartmentDeadlineData(
                            organization,
                            dateRange.startDate,
                            dateRange.endDate,
                        )
                    }
                    toast.success(
                        created.length === 1
                            ? '1 deadline created'
                            : `${created.length} deadlines created`,
                    )
                    setBulkDeadlineModalOpen(false)
                })
                .catch((e) => {
                    toast.error(
                        e?.response?.data?.detail ??
                            e?.message ??
                            'Failed to create deadlines',
                    )
                })
        },
        [
            createBulkDeadlines,
            organization,
            dateRange.startDate,
            dateRange.endDate,
            getApartmentDeadlineData,
        ],
    )

    // Initial load - only when organization changes
    useEffect(() => {
        if (organization) {
            const initialDateRange = getDateRangeFromTimePeriod(timePeriod)
            getApartmentDeadlineData(
                organization,
                initialDateRange.startDate,
                initialDateRange.endDate,
            )
        }
    }, [organization])

    // Handle time period change - trigger API call
    const handleTimePeriodChange = useCallback(
        (newTimePeriod: string) => {
            setTimePeriod(newTimePeriod)
            if (organization) {
                const newDateRange = getDateRangeFromTimePeriod(
                    newTimePeriod,
                    customStartDate || undefined,
                    customEndDate || undefined,
                )
                getApartmentDeadlineData(
                    organization,
                    newDateRange.startDate,
                    newDateRange.endDate,
                )
            }
        },
        [organization, customStartDate, customEndDate],
    )

    // Handle custom date changes - trigger API call (only when both dates are valid)
    const handleCustomDateChange = useCallback(
        (startDate: Date | null, endDate: Date | null) => {
            setCustomStartDate(startDate)
            setCustomEndDate(endDate)
            const startValid = startDate != null && !isNaN(startDate.getTime())
            const endValid = endDate != null && !isNaN(endDate.getTime())
            if (
                organization &&
                timePeriod === 'custom' &&
                startValid &&
                endValid
            ) {
                const newDateRange = getDateRangeFromTimePeriod(
                    'custom',
                    startDate,
                    endDate,
                )
                getApartmentDeadlineData(
                    organization,
                    newDateRange.startDate,
                    newDateRange.endDate,
                )
            }
        },
        [organization, timePeriod],
    )

    // Map backend data to Property format
    const properties = useMemo(() => {
        return deadlines
            .map(mapDeadlineToProperty)
            .filter((p): p is Property => p !== null)
    }, [deadlines])

    // Get the selected tag's apartment IDs for filtering
    const selectedTagApartmentIds = useMemo(() => {
        if (!selectedTagId) return null
        const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
        return selectedTag?.apartments || null
    }, [selectedTagId, userTags])

    // Filter properties based on search query and tag
    const filteredProperties = useMemo(() => {
        let filtered = properties

        // Filter by tag (property must be in the tag's apartments list)
        if (selectedTagApartmentIds) {
            filtered = filtered.filter((p) =>
                selectedTagApartmentIds.includes(p.id),
            )
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim()
            filtered = filtered.filter(
                (p) =>
                    p.name.toLowerCase().includes(query) ||
                    (p.location && p.location.toLowerCase().includes(query)),
            )
        }

        return filtered
    }, [properties, selectedTagApartmentIds, searchQuery])

    // Sort filtered properties - optimized for performance
    const sortedProperties = useMemo(() => {
        if (filteredProperties.length === 0) return filteredProperties

        // Pre-calculate sort values to avoid repeated calculations
        const withSortValues = filteredProperties.map((p) => {
            let sortValue: number | string
            switch (sortKey) {
                case 'name':
                    sortValue = p.name.toLowerCase()
                    break
                case 'progress':
                    sortValue =
                        p.totalTasks > 0 ? p.completedTasks / p.totalTasks : 0
                    break
                case 'completedTasks':
                    sortValue = p.completedTasks
                    break
                case 'overdueTasks':
                    sortValue = p.overdueTasks
                    break
                case 'status': {
                    const statusOrder: Record<Property['status'], number> = {
                        'on-track': 0,
                        'at-risk': 1,
                        behind: 2,
                    }
                    sortValue = statusOrder[p.status]
                    break
                }
                default:
                    sortValue = 0
            }
            return { property: p, sortValue }
        })

        // Sort with pre-calculated values
        withSortValues.sort((a, b) => {
            let comparison: number
            if (
                typeof a.sortValue === 'string' &&
                typeof b.sortValue === 'string'
            ) {
                // Fast string comparison
                comparison =
                    a.sortValue < b.sortValue
                        ? -1
                        : a.sortValue > b.sortValue
                        ? 1
                        : 0
            } else {
                comparison = (a.sortValue as number) - (b.sortValue as number)
            }
            return sortDirection === 'asc' ? comparison : -comparison
        })

        return withSortValues.map((item) => item.property)
    }, [filteredProperties, sortKey, sortDirection])

    // Use pagination hook
    const pagination = usePagination(sortedProperties, 10)

    // Reset to first page when search query, tag, or sort changes
    useEffect(() => {
        pagination.setPage(1)
    }, [searchQuery, selectedTagId, sortKey, sortDirection])

    const handleSort = useCallback(
        (key: SortKey) => {
            if (sortKey === key) {
                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
            } else {
                setSortKey(key)
                setSortDirection('asc')
            }
        },
        [sortKey, sortDirection],
    )

    const totalCompleted = filteredProperties.reduce(
        (sum, p) => sum + p.completedTasks,
        0,
    )
    const totalTasks = filteredProperties.reduce(
        (sum, p) => sum + p.totalTasks,
        0,
    )
    const totalOverdue = filteredProperties.reduce(
        (sum, p) => sum + p.overdueTasks,
        0,
    )
    const onTrackCount = filteredProperties.filter(
        (p) => p.status === 'on-track',
    ).length
    // Properties at risk = all properties not on track (includes 'at-risk' and 'behind')
    const atRiskCount = filteredProperties.filter(
        (p) => p.status !== 'on-track',
    ).length
    const behindCount = filteredProperties.filter((p) => p.status === 'behind')
        .length

    const handleRefresh = useCallback(() => {
        if (organization) {
            getApartmentDeadlineData(
                organization,
                dateRange.startDate,
                dateRange.endDate,
            )
                .then(() => {
                    toast.success('Dashboard refreshed')
                })
                .catch(() => {
                    toast.error('Failed to refresh dashboard')
                })
        }
    }, [organization, dateRange.startDate, dateRange.endDate])

    const handlePropertyClick = useCallback(
        (property: Property) => {
            setSelectedProperty(property)
            // Find the original deadline data to get the full deadlines array
            const originalDeadlineData = deadlines.find(
                (d) => d.id === property.id,
            )
            setSelectedDeadlineData(originalDeadlineData || null)
            setIsDrawerOpen(true)
        },
        [deadlines],
    )

    return (
        <Box className={classes.root}>
            <DashboardHeader
                timePeriod={timePeriod}
                setTimePeriod={handleTimePeriodChange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomDateChange={handleCustomDateChange}
                selectedTagId={selectedTagId}
                setSelectedTagId={setSelectedTagId}
                availableTags={userTags}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onRefresh={handleRefresh}
                onAddDeadline={() => setAddDeadlineModalOpen(true)}
                onBulkDeadline={() => setBulkDeadlineModalOpen(true)}
            />

            <Box component="main" className={classes.main}>
                {loading ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* KPI Row */}
                        <Grid container spacing={3} className={classes.kpiGrid}>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="Target Completed Items"
                                    value={totalCompleted}
                                    subtitle={`of ${totalTasks} total tasks`}
                                    icon="target"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="Properties On Track"
                                    value={onTrackCount}
                                    subtitle={`${
                                        filteredProperties.length > 0
                                            ? Math.round(
                                                  (onTrackCount /
                                                      filteredProperties.length) *
                                                      100,
                                              )
                                            : 0
                                    }% of portfolio`}
                                    icon="check"
                                    variant="success"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="Properties At Risk"
                                    value={atRiskCount}
                                    subtitle="Require attention"
                                    icon="warning"
                                    variant="warning"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="Total Overdue Tasks"
                                    value={totalOverdue}
                                    subtitle={`Across ${behindCount} properties`}
                                    icon="error"
                                    variant="danger"
                                />
                            </Grid>
                        </Grid>

                        {/* Main Grid */}
                        <Grid
                            container
                            spacing={3}
                            className={classes.tableContainer}
                        >
                            <Grid item xs={12}>
                                <PortfolioTable
                                    properties={pagination.pageData}
                                    onPropertyClick={handlePropertyClick}
                                    pagination={pagination}
                                    totalCount={sortedProperties.length}
                                    sortKey={sortKey}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Box>

            {/* Property Detail Drawer */}
            <DeadlinePropertyDrawer
                property={selectedProperty}
                deadlineData={selectedDeadlineData as any}
                isOpen={isDrawerOpen}
                onClose={() => {
                    setIsDrawerOpen(false)
                    setSelectedProperty(null)
                    setSelectedDeadlineData(null)
                }}
            />

            {/* Add Deadline Modal */}
            <AddDeadlineModal
                theme={theme}
                formState={addDeadlineFormState}
                onClose={() => setAddDeadlineModalOpen(false)}
                onCreate={handleCreateDeadline}
                autoSelectCurrentWorkspace={false}
            />

            {/* Bulk Deadline Modal */}
            <BulkDeadlineModal
                open={bulkDeadlineModalOpen}
                onClose={() => setBulkDeadlineModalOpen(false)}
                onCreateBulk={handleBulkCreateDeadlines}
                bulkLoading={deadlineLoadingState.createBulk}
                theme={theme}
            />
        </Box>
    )
}
